import Color from '@/Enums/Color.enum';
import ScreenAccess from '@/Enums/Screens/ScreenAccess.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import CloseButton from '@/components/Modal/common/CloseButton';
import ModalBody from '@/components/Modal/common/ModalBody';
import { SIDEBAR_SCREEN_LABELS } from '@/constants/routes';
import { isAdminRole } from '@/helpers/roles';
import { bulkUpdateScreenAccess } from '@/pages/UserManagement/UserModals/data/userService';
import { useAppSelector } from '@/store/hooks';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';

// UI-only routes that have no matching backend screen and so can't be granted here.
const UI_ONLY_SCREENS = [ScreenKey.CREATE_CAMPAIGN, ScreenKey.FORECASTER_TABLE, ScreenKey.SETTINGS];
const GRANTABLE_SCREENS = Object.values(ScreenKey).filter(screen => !UI_ONLY_SCREENS.includes(screen));

/**
 * Admin-type roles (Super Admin / Org Admin / Whitelabel Admin) manage access
 * per screen as a grant/no-grant — no Read tier — a deliberately narrower
 * option set than Client/Agency/Advertiser's Full/Read/Revoke, kept distinct
 * from the individual-edit form (which unified to one Off/Read/Full control
 * for every user type per flank#4749/#4750). Bulk apply still offers the two
 * option sets below, matched to whichever type is selected (#4443, Martin) —
 * not revisited here since neither ticket mentions bulk-apply.
 */
const ADMIN_ACCESS_OPTIONS = [
  { value: ScreenAccess.Full, label: 'Grant access' },
  { value: 'revoke', label: 'Revoke access' },
];
const CLIENT_ACCESS_OPTIONS = [
  { value: ScreenAccess.Full, label: 'Full access' },
  { value: ScreenAccess.Read, label: 'Read only' },
  { value: 'revoke', label: 'No access (revoke)' },
];

// Same chunking AccessManagement/index.jsx uses so the bulk checklist wraps
// the same way the individual-edit screen list does (#4443, Martin: bulk
// should read like the same screen, not a different, dropdown-based one).
const DEFAULT_COLUMN_SIZE = 4;
const MAX_COLUMNS = 3;
const getScreenColumns = (screens: ScreenKey[]) => {
  const chunks: ScreenKey[][] = [];
  const columnSize = Math.max(DEFAULT_COLUMN_SIZE, Math.ceil(screens.length / MAX_COLUMNS));
  for (let i = 0; i < screens.length; i += columnSize) {
    chunks.push(screens.slice(i, i + columnSize));
  }
  return chunks;
};

/**
 * Which screens are worth offering for a bulk change: the union of every
 * selected role's own `screensByRole` entry, so the list never offers a
 * screen none of the selected users could ever be granted (#4443, Josh/Martin
 * follow-up). Falls back to the full grantable list for a role that hasn't
 * loaded into `screensByRole` yet, rather than silently hiding it.
 */
export const getBulkGrantableScreens = (
  selectedRoles: number[],
  screensByRole: Record<number, Array<{ value: ScreenKey }>>,
): ScreenKey[] => {
  if (selectedRoles.length === 0) return [];
  const union = new Set<ScreenKey>();
  for (const role of selectedRoles) {
    const roleScreens = screensByRole[role];
    if (!roleScreens || roleScreens.length === 0) {
      for (const screen of GRANTABLE_SCREENS) union.add(screen);
      continue;
    }
    for (const { value } of roleScreens) {
      if (!UI_ONLY_SCREENS.includes(value)) union.add(value);
    }
  }
  return GRANTABLE_SCREENS.filter(screen => union.has(screen));
};

interface IProps {
  open: boolean;
  handleClose: () => void;
  userIds: number[];
  /**
   * Role by user id, accumulated across every page the grid has loaded (not
   * just the current one) — a selection can span several pages of
   * server-side pagination (`keepNonExistentRowsSelected`), so this can't be
   * derived from only the currently-loaded rows.
   */
  roleByUserId: Record<number, number | undefined>;
}

const BulkPermissionModal = ({ open, handleClose, userIds, roleByUserId }: IProps) => {
  const { screensByRole } = useAppSelector(state => state.userManagement);
  const [screens, setScreens] = useState<ScreenKey[]>([]);
  const [access, setAccess] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedRoles = userIds.map(id => roleByUserId[id]).filter((role): role is number => role !== undefined);
  const roleTypes = new Set(selectedRoles.map(role => (isAdminRole(role) ? 'admin' : 'client')));
  // A single-type check even with zero users selected — the modal only opens
  // from a non-empty selection, so an empty set here never has to mean "mixed".
  const isMixedRoleType = roleTypes.size > 1;
  const isAdminSelection = roleTypes.has('admin');
  const accessOptions = isAdminSelection ? ADMIN_ACCESS_OPTIONS : CLIENT_ACCESS_OPTIONS;
  const availableScreens = getBulkGrantableScreens(selectedRoles, screensByRole);
  const screenColumns = getScreenColumns(availableScreens);

  const toggleScreen = (screen: ScreenKey, checked: boolean) => {
    setScreens(prev => (checked ? [...prev, screen] : prev.filter(s => s !== screen)));
  };

  const reset = () => {
    setScreens([]);
    setAccess('');
    setConfirmOpen(false);
  };

  const onClose = () => {
    reset();
    handleClose();
  };

  const handleApply = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const { data } = await bulkUpdateScreenAccess(userIds, screens, access);
      const updated = data.updated ?? [];
      const failed = data.failed ?? [];
      if (updated.length) {
        toast.success(`Updated ${updated.length} user(s)`);
      }
      if (failed.length) {
        const reasons = failed.map((f: { id: number; reason: string }) => f.reason).join('; ');
        toast.error(`Failed for ${failed.length} user(s): ${reasons}`);
      }
      onClose();
    } catch {
      // A rejected request doesn't prove nothing saved — the server may have
      // applied some or all of the change before a timeout/connection drop.
      toast.error("Couldn't confirm this went through — some users may have been updated. Refresh before retrying.");
    } finally {
      setSubmitting(false);
    }
  };

  const accessLabel = accessOptions.find(o => o.value === access)?.label;
  const screenLabels = screens.map(s => SIDEBAR_SCREEN_LABELS[s]).join(', ');

  return (
    <>
      <Modal open={open && !confirmOpen} onClose={onClose}>
        <ModalBody>
          <div className='flex items-center justify-between mb-4'>
            <p className='text-xl font-semibold'>
              Apply Permission to {userIds.length} User{userIds.length === 1 ? '' : 's'}
            </p>
            <CloseButton onClick={onClose} />
          </div>

          {isMixedRoleType ? (
            <Alert severity='warning'>
              Selected users mix Admin-type roles (Super Admin, Org Admin, Whitelabel Admin) with
              Client/Agency/Advertiser roles — those use different access models. Select users of one type at a time.
            </Alert>
          ) : (
            <div className='flex flex-col gap-4'>
              <div>
                <p className='text-sm font-medium mb-2'>Screens</p>
                <div className='flex gap-[8px]'>
                  {screenColumns.map((column, col) => (
                    <FormGroup key={col}>
                      {column.map(screen => (
                        <FormControlLabel
                          key={screen}
                          label={SIDEBAR_SCREEN_LABELS[screen]}
                          control={
                            <Checkbox
                              checked={screens.includes(screen)}
                              onChange={e => toggleScreen(screen, e.target.checked)}
                            />
                          }
                        />
                      ))}
                    </FormGroup>
                  ))}
                </div>
              </div>

              <div>
                <p className='text-sm font-medium mb-2'>Access level</p>
                <ToggleButtonGroup
                  value={access}
                  exclusive
                  disabled={screens.length === 0}
                  onChange={(_, newValue: string | null) => {
                    if (newValue !== null) setAccess(newValue);
                  }}
                  size='small'
                  sx={{
                    background: 'white',
                    borderRadius: '8px',
                    gap: '2px',
                    '& .MuiToggleButton-root': {
                      fontSize: '0.8rem',
                      border: `1px solid ${Color.LightGray}`,
                      borderRadius: '8px',
                      textTransform: 'none',
                      padding: '6px 14px',
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${Color.Primary} !important`,
                      color: 'white',
                    },
                  }}
                >
                  {accessOptions.map(option => (
                    <ToggleButton key={option.value} value={option.value}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </div>
            </div>
          )}

          <div className='flex justify-end gap-[9px] mt-6'>
            <Button
              variant='contained'
              disabled={isMixedRoleType || screens.length === 0 || !access || submitting}
              onClick={() => setConfirmOpen(true)}
            >
              Apply
            </Button>
            <Button variant='outlined' color='error' onClick={onClose}>
              Cancel
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleApply}
        text={`Set "${screenLabels}" to "${accessLabel}" for ${userIds.length} user${userIds.length === 1 ? '' : 's'}?`}
        confirmButtonText='Apply'
      />
    </>
  );
};

export default BulkPermissionModal;
